// Admin dashboard — Active Sessions modal + its two confirmation dialogs.
// Pure structural extraction from AdminDashboard.jsx (~275 lines JSX).
// Zero behaviour/UI change: every data-testid, class, accessible label,
// and event handler wiring is preserved verbatim — the parent still owns
// the state and business logic (loadSessions / revokeSession /
// revokeAllSessions) and passes them in as props. Nothing new is
// introduced; nothing is removed.

import React from "react";
import {
  MonitorSmartphone, Monitor, Smartphone, Tablet,
  X, LogOut, Loader2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { formatRelativeTime } from "./utils";

export default function SessionsModals({
  sessionsOpen,
  setSessionsOpen,
  sessionsLoading,
  sessionsList,
  sessionsBusy,
  loadSessions,
  confirmRevokeSession,
  setConfirmRevokeSession,
  confirmRevokeAll,
  setConfirmRevokeAll,
  revokeSession,
  revokeAllSessions,
}) {
  return (
    <>
      {sessionsOpen && (
        <div
          data-testid="sessions-modal"
          className="fixed inset-0 z-[65] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => { if (!sessionsBusy) setSessionsOpen(false); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="sessions-modal-title"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5 sm:p-7 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-5">
              <div className="rounded-full bg-maroon/10 p-2.5 text-maroon flex-shrink-0">
                <MonitorSmartphone size={18} strokeWidth={1.7} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="overline text-[10px]">Security</p>
                <h3 id="sessions-modal-title" className="font-serif text-xl text-ink mt-1 leading-tight">
                  Active Sessions
                </h3>
                <p className="text-[12px] text-muted2 mt-1.5 leading-relaxed">
                  Every device currently signed into Tanish Textiles — across both SIDER and BRIDAL.
                </p>
              </div>
              <button
                data-testid="sessions-close-btn"
                onClick={() => { if (!sessionsBusy) setSessionsOpen(false); }}
                className="text-muted2 hover:text-maroon transition-colors p-1 -m-1 flex-shrink-0"
                aria-label="Close"
              >
                <X size={16} strokeWidth={1.7} />
              </button>
            </div>

            {sessionsLoading ? (
              <div className="text-center py-10 text-muted2" data-testid="sessions-loading">
                <Loader2 className="mx-auto animate-spin" size={20} />
              </div>
            ) : !sessionsList || sessionsList.length === 0 || sessionsList.every(g => !g.sessions?.length) ? (
              <div className="text-center py-10 text-muted2 text-sm" data-testid="sessions-empty">
                No active sessions.
              </div>
            ) : (
              <div data-testid="sessions-list" className="space-y-5">
                {sessionsList.map((group, gi) => {
                  // "Freshest activity" summary — max last_used_at across
                  // this panel's currently-active sessions. Gives you an
                  // at-a-glance recency read without expanding each row.
                  const lastActive = (group.sessions || [])
                    .map((s) => s.last_used_at)
                    .filter(Boolean)
                    .sort()
                    .slice(-1)[0];
                  return (
                  <section
                    key={group.role || group.panel || gi}
                    data-testid={`sessions-group-${group.role || gi}`}
                  >
                    <header className="flex items-baseline justify-between gap-2 mb-2 px-0.5">
                      <div className="min-w-0">
                        <p className="overline text-[10px] text-maroon">{group.panel || "Panel"}</p>
                        <p className="text-[12px] text-ink truncate" title={group.email}>
                          {group.email || "—"}
                        </p>
                        <p
                          data-testid={`sessions-group-lastactive-${group.role || gi}`}
                          className="text-[11px] text-muted2 mt-0.5"
                        >
                          {lastActive
                            ? `Last signed in ${formatRelativeTime(lastActive).toLowerCase()}`
                            : "No sign-ins"}
                        </p>
                      </div>
                      <span className="text-[10px] text-muted2 flex-shrink-0">
                        {(group.sessions || []).length} active
                      </span>
                    </header>
                    {(!group.sessions || group.sessions.length === 0) ? (
                      <div className="border border-line rounded-xl px-4 py-4 text-[12px] text-muted2 bg-ivory/40 text-center">
                        No devices signed in
                      </div>
                    ) : (
                      <ul className="divide-y divide-line border border-line rounded-xl overflow-hidden">
                        {group.sessions.map((s) => {
                          const isMobile = /iOS|iPadOS|Android/i.test(s.os || "");
                          const isTablet = /iPad|Tablet/i.test(s.device_label || "");
                          const Icon = isTablet ? Tablet : isMobile ? Smartphone : Monitor;
                          const label = s.device_label || "Unknown device";
                          const subtitle = [s.browser, s.os].filter(Boolean).join(" · ");
                          return (
                            <li
                              key={s.id}
                              data-testid={`session-row-${s.id}`}
                              className={`px-4 py-3.5 flex items-start gap-3 ${s.current ? "bg-maroon/[0.04]" : ""}`}
                            >
                              <div className="rounded-full bg-ivory border border-line p-2 text-maroon flex-shrink-0">
                                <Icon size={16} strokeWidth={1.7} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm text-ink font-medium leading-tight">{label}</p>
                                  {s.current && (
                                    <span
                                      data-testid={`session-current-${s.id}`}
                                      className="inline-flex items-center gap-1 rounded-full bg-maroon/[0.10] text-maroon text-[10px] font-semibold uppercase tracking-[0.16em] px-2 py-0.5 border border-maroon/25"
                                    >
                                      This device
                                    </span>
                                  )}
                                </div>
                                {subtitle && (
                                  <p className="text-[12px] text-muted2 mt-0.5">{subtitle}</p>
                                )}
                                <p className="text-[11px] text-muted2 mt-1.5">
                                  Last active: {formatRelativeTime(s.last_used_at)}
                                </p>
                              </div>
                              {!s.current && (
                                <button
                                  data-testid={`session-signout-${s.id}`}
                                  onClick={() => setConfirmRevokeSession({ ...s, panel: group.panel, email: group.email })}
                                  disabled={sessionsBusy === s.id}
                                  className="btn-outline-maroon !px-3 !py-1.5 !text-[11px] disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                                >
                                  {sessionsBusy === s.id ? (
                                    <Loader2 size={11} strokeWidth={1.9} className="animate-spin" />
                                  ) : (
                                    "Sign Out"
                                  )}
                                </button>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </section>
                  );
                })}
              </div>
            )}

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2">
              <button
                data-testid="sessions-refresh-btn"
                onClick={loadSessions}
                disabled={sessionsLoading}
                className="text-[11px] text-muted2 hover:text-maroon inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} strokeWidth={1.7} /> Refresh
              </button>
              <button
                data-testid="sessions-signout-all-btn"
                onClick={() => setConfirmRevokeAll(true)}
                disabled={!sessionsList || sessionsList.every(g => !g.sessions?.length) || !!sessionsBusy}
                className="btn-outline-maroon !px-4 !py-2 !text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LogOut size={12} strokeWidth={1.7} /> Sign Out of All Devices
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm sign-out of a single OTHER device */}
      {confirmRevokeSession && (
        <div
          data-testid="confirm-revoke-session-modal"
          className="fixed inset-0 z-[75] flex items-center justify-center bg-black/45 backdrop-blur-sm px-4"
          onClick={() => { if (sessionsBusy !== confirmRevokeSession?.id) setConfirmRevokeSession(null); }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-maroon/10 p-2.5 text-maroon flex-shrink-0">
                <LogOut size={18} strokeWidth={1.7} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="overline text-[10px]">Sign out device</p>
                <h3 className="font-serif text-xl text-ink mt-1 leading-tight">Sign out this device?</h3>
                <p className="text-sm text-muted2 mt-3 leading-relaxed">
                  This will immediately end the active session on{" "}
                  <span className="text-ink font-medium">
                    {confirmRevokeSession.device_label || "this device"}
                    {confirmRevokeSession.browser ? ` · ${confirmRevokeSession.browser}` : ""}
                  </span>
                  {confirmRevokeSession.panel ? (
                    <> for <span className="text-ink font-medium">{confirmRevokeSession.panel}</span></>
                  ) : null}
                  .
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                data-testid="confirm-revoke-session-cancel"
                onClick={() => setConfirmRevokeSession(null)}
                disabled={sessionsBusy === confirmRevokeSession?.id}
                className="btn-outline-maroon !px-4 !py-2 !text-xs disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-revoke-session-confirm"
                onClick={() => revokeSession(confirmRevokeSession)}
                disabled={sessionsBusy === confirmRevokeSession?.id}
                className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {sessionsBusy === confirmRevokeSession?.id ? (
                  <><Loader2 size={13} strokeWidth={1.9} className="animate-spin" /> Signing out…</>
                ) : (
                  <><LogOut size={13} strokeWidth={1.9} /> Sign Out</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm sign-out of ALL devices */}
      {confirmRevokeAll && (
        <div
          data-testid="confirm-revoke-all-modal"
          className="fixed inset-0 z-[75] flex items-center justify-center bg-black/45 backdrop-blur-sm px-4"
          onClick={() => { if (sessionsBusy !== "all") setConfirmRevokeAll(false); }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-[#F1DCDC] p-2.5 text-[#8A2A2A] flex-shrink-0">
                <AlertTriangle size={18} strokeWidth={1.7} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="overline text-[10px]">Sign out everywhere</p>
                <h3 className="font-serif text-xl text-ink mt-1 leading-tight">Sign out of all devices?</h3>
                <p className="text-sm text-muted2 mt-3 leading-relaxed">
                  This will end <span className="text-ink font-medium">all active Tanish Textiles admin sessions across SIDER and BRIDAL</span>, including this device. Every signed-in device will need to log in again.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                data-testid="confirm-revoke-all-cancel"
                onClick={() => setConfirmRevokeAll(false)}
                disabled={sessionsBusy === "all"}
                className="btn-outline-maroon !px-4 !py-2 !text-xs disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-revoke-all-confirm"
                onClick={revokeAllSessions}
                disabled={sessionsBusy === "all"}
                className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {sessionsBusy === "all" ? (
                  <><Loader2 size={13} strokeWidth={1.9} className="animate-spin" /> Signing out…</>
                ) : (
                  <><LogOut size={13} strokeWidth={1.9} /> Sign Out of All Devices</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
